if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];
      variantImagePreloadCache = new Set();
      variantImagePreloadStarted = false;

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.initReadmoreCut();
        this.initVariantImagePreload();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initVariantImagePreload() {
        if (this.variantImagePreloadStarted) return;

        const preloadScript = this.querySelector('[data-variant-image-preload]');
        if (!preloadScript) return;

        let payload = null;
        try {
          payload = JSON.parse(preloadScript.textContent || '{}');
        } catch (error) {
          console.warn('Variant image preload payload parse failed', error);
          return;
        }

        const variantEntries = Array.isArray(payload?.variants) ? payload.variants : [];
        if (!variantEntries.length) return;

        const selectedVariantId =
          payload?.selectedVariantId === null || payload?.selectedVariantId === undefined
            ? null
            : String(payload.selectedVariantId);
        const selectedEntry =
          variantEntries.find((entry) => String(entry?.id) === selectedVariantId) || variantEntries[0];
        const selectedEntryId =
          selectedEntry?.id === null || selectedEntry?.id === undefined ? null : String(selectedEntry.id);

        const selectedImages = this.uniqueImageUrls(selectedEntry?.images);
        const otherImages = this.uniqueImageUrls(
          variantEntries
            .filter((entry) => String(entry?.id) !== selectedEntryId)
            .flatMap((entry) => (Array.isArray(entry?.images) ? entry.images : [])),
          new Set(selectedImages)
        );

        if (!selectedImages.length && !otherImages.length) return;

        this.variantImagePreloadStarted = true;
        this.preloadImages(selectedImages).finally(() => {
          if (!otherImages.length) return;

          const startOtherVariantPreload = () => this.preloadImages(otherImages, 2);
          if ('requestIdleCallback' in window) {
            window.requestIdleCallback(startOtherVariantPreload, { timeout: 1500 });
            return;
          }
          window.setTimeout(startOtherVariantPreload, 150);
        });
      }

      uniqueImageUrls(imageUrls, seen = new Set()) {
        const normalizedUrls = [];
        if (!Array.isArray(imageUrls)) return normalizedUrls;

        imageUrls.forEach((url) => {
          if (typeof url !== 'string') return;
          const normalizedUrl = url.trim();
          if (!normalizedUrl || seen.has(normalizedUrl)) return;
          seen.add(normalizedUrl);
          normalizedUrls.push(normalizedUrl);
        });

        return normalizedUrls;
      }

      preloadImages(imageUrls, concurrency = 3) {
        if (!Array.isArray(imageUrls) || !imageUrls.length) return Promise.resolve();

        const maxConcurrency = Math.max(1, Math.min(concurrency, 6));
        let currentIndex = 0;

        const runWorker = () => {
          if (currentIndex >= imageUrls.length) return Promise.resolve();
          const url = imageUrls[currentIndex];
          currentIndex += 1;
          return this.preloadImage(url).finally(runWorker);
        };

        return Promise.allSettled(
          Array.from({ length: Math.min(maxConcurrency, imageUrls.length) }, () => runWorker())
        );
      }

      preloadImage(url) {
        if (!url || this.variantImagePreloadCache.has(url)) {
          return Promise.resolve();
        }

        this.variantImagePreloadCache.add(url);

        return new Promise((resolve) => {
          const image = new Image();
          image.decoding = 'async';
          image.onload = () => resolve();
          image.onerror = () => resolve();
          image.src = url;
        });
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
          this.initReadmoreCut(newNode);
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        const sectionId = this.dataset.section;
        const mediaGallerySource =
          this.querySelector(`#MediaGallery-${sectionId}`) || this.querySelector('media-gallery');
        const mediaGalleryDestination =
          html.querySelector(`#MediaGallery-${sectionId}`) || html.querySelector('media-gallery');

        if (mediaGallerySource && mediaGalleryDestination) {
          // Replace the whole gallery so variant-specific media/thumbnail markup
          // is always in sync, including structures that appear/disappear.
          mediaGallerySource.outerHTML = mediaGalleryDestination.outerHTML;
        }

        const mediaGallery =
          this.querySelector(`#MediaGallery-${sectionId}`) || this.querySelector('media-gallery');
        const mediaGalleryList = mediaGallery?.querySelector(`#Slider-Gallery-${sectionId}`) || mediaGallery?.querySelector('[id^="Slider-Gallery-"]');

        if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover();

        // keep media in sync even when a variant has no featured_media;
        // in that case, activate the first gallery item.
        const fallbackMediaItem =
          mediaGalleryList?.querySelector('li[data-media-id]:not([style*="display: none"])') ||
          mediaGalleryList?.querySelector('li[data-media-id]');
        const fallbackMediaId = fallbackMediaItem?.dataset.mediaId;
        const activeMediaId = variantFeaturedMediaId ? `${sectionId}-${variantFeaturedMediaId}` : fallbackMediaId;

        if (activeMediaId) {
          mediaGallery?.setActiveMedia?.(activeMediaId, true);
        }

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      initReadmoreCut(root = this) {
        if (!root || !root.querySelectorAll) return;

        const labelSource =
          root instanceof HTMLElement && root.hasAttribute('data-read-more-label') ? root : this;
        const readMoreLabel = labelSource.dataset.readMoreLabel || 'Read more';
        const showLessLabel = labelSource.dataset.showLessLabel || 'Show less';

        const bindReadmoreToggle = (button, extra) => {
          if (!button || !extra) return;

          const setState = (isOpen) => {
            button.setAttribute('aria-expanded', String(isOpen));
            button.textContent = isOpen ? showLessLabel : readMoreLabel;
            const stickyContainer = button.closest('.product__column-sticky');
            if (stickyContainer) {
              stickyContainer.classList.toggle('ggs-readmore-expanded', isOpen);
            }
          };

          setState(extra.classList.contains('is-open'));

          button.addEventListener('click', () => {
            const isOpen = extra.classList.toggle('is-open');
            setState(isOpen);

            if (isOpen) {
              const scrollToExpandedEnd = () => {
                const target = extra.lastElementChild || extra;
                target.scrollIntoView({
                  behavior: 'smooth',
                  block: 'end',
                  inline: 'nearest',
                });
              };

              requestAnimationFrame(scrollToExpandedEnd);
              setTimeout(scrollToExpandedEnd, 220);
            }
          });
        };

        const descriptions = root.querySelectorAll('.product__description');
        descriptions.forEach((description) => {
          if (description.dataset.readmoreCutInit === '1') return;

          const content =
            description.querySelector('.ggs-desc__content') ||
            description.querySelector('.rte') ||
            description;
          const marker = content.querySelector('.readmore-cut');
          const existingButton = content.querySelector('.readmore-btn');
          const existingExtra = content.querySelector('.readmore-extra');

          if (!marker && existingButton && existingExtra) {
            description.dataset.readmoreCutInit = '1';
            existingExtra.classList.remove('is-open');
            bindReadmoreToggle(existingButton, existingExtra);
            return;
          }

          if (!marker) return;

          description.dataset.readmoreCutInit = '1';

          const ggsWrap = marker.closest('.ggs-desc');
          if (ggsWrap) {
            ggsWrap.classList.add('ggs-desc--cut');
            ggsWrap.classList.remove('ggs-desc--clamped');
          }

          const extra = document.createElement('div');
          extra.className = 'readmore-extra';
          const range = document.createRange();
          range.setStartAfter(marker);
          range.setEndAfter(content.lastChild);
          extra.appendChild(range.extractContents());

          const hasExtraContent = extra.textContent.trim().length > 0 || extra.querySelector('*');
          if (!hasExtraContent) {
            extra.remove();
            marker.remove();
            return;
          }

          marker.parentNode.insertBefore(extra, marker.nextSibling);

          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'readmore-btn';
          marker.parentNode.insertBefore(button, extra);

          marker.remove();
          bindReadmoreToggle(button, extra);
        });
      }
    }
  );
}

